import { useEffect, useState } from 'react';
import Icon from '../../components/Icon';
import { ErrorBanner, InfoBanner, Toast } from '../../components/Feedback';
import { api } from '../../api/client';

/**
 * Teacher view: the codes this school still has to hand out, per title.
 *
 * <p>Redeemed codes are counted but never listed — a teacher has no reason to read
 * back a code a student has already used, and every extra copy of a live code is one
 * more place it can leak from.
 */
export default function TeacherCodesPage() {
  const [pools, setPools] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.myCodes().then(setPools).catch(setError);
  }, []);

  async function copy(pool) {
    try {
      await navigator.clipboard.writeText(pool.codes.join('\n'));
      setToast(`${pool.codes.length} code(s) copied`);
    } catch {
      setToast('Your browser blocked the copy — select the codes and copy them by hand');
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Student Codes</h1>
        <p>
          Give each student one code. A code works once, and only for a student at
          this school.
        </p>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {pools === null && <div className="empty">Loading…</div>}

      {/* Say which of the three preconditions failed, not just that the list is
          empty — the remedy is different for each, and so is who can apply it. */}
      {pools !== null && pools.pools.length === 0 && (
        <InfoBanner>
          {pools.message ?? 'There are no codes to hand out yet.'}
        </InfoBanner>
      )}

      {pools?.pools.map((pool) => (
        <section className="card" key={pool.titleId} style={{ marginBottom: 'var(--s1)' }}>
          <div className="pool">
            <div className="pool-head">
              <div>
                <h3>{pool.titleName}</h3>
                <p className="muted small" style={{ margin: '2px 0 0' }}>
                  {/* A title's class is already "Class 8"; prefixing doubled it. */}
                  {pool.classLevel ? `${pool.classLevel} · ` : ''}
                  {pool.used} of {pool.allocated} used
                </p>
              </div>
              {pool.codes.length > 0 && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => copy(pool)}>
                  <Icon name="copy" size={16} /> Copy all
                </button>
              )}
            </div>

            {pool.codes.length === 0 ? (
              <p className="muted small" style={{ margin: 0 }}>
                Every code for this title has been used.
              </p>
            ) : (
              <>
                <ul className="code-grid">
                  {pool.codes.map((code) => (
                    <li key={code} className="code-text">
                      {code}
                    </li>
                  ))}
                </ul>
                {pool.truncated && (
                  <p className="hint">
                    Showing the first {pool.codes.length} of {pool.unused} unused codes.
                  </p>
                )}
              </>
            )}
          </div>
        </section>
      ))}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2200)} />
    </>
  );
}
